import React from 'react';
import {
  AlertDialog,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  SearchableSelectV2 as SearchableSelect,
} from '@amfintech/react-admin-ui';
import { Content, createHelpers } from '@amfintech/refine-react-admin';
import { useCustomMutation, useInvalidate, useLink, useNavigation, useCustom } from '@refinedev/core';
import dayjs from 'dayjs';

import { TomswinSyncBatch, TomswinSyncBatchStatus } from '~/api';
import { resourceNames } from '~/resources/resource-names';
 
const { createTableComponent } =
  createHelpers<TomswinSyncBatch>({
    resourceName: resourceNames.tomswinSyncBatch,
  });

export default function DeveloperToolsDashboard() {
  const Link = useLink();
  const { listUrl } = useNavigation();
  const invalidate = useInvalidate();

  const customMutation = useCustomMutation();

  const allowedFileTypes = [
    'FMD_FUND',
    'FMD_FUND_POL',
    'FMD_PF',
    'FMD_NAV',
    'FMD_CAL',
    'FMD_CALF',
    'FMD_CIF',
    'FMD_TRUSTEE',
    'FMD_MACC',
    'FMD_INVBNK',
    'FMD_UTACC',
    'FMD_TRX_HIST',
    'FMD_TXN_STATUS',
    'FMD_BANK',
    'FMD_ADR',
    'FundFullList',
    'FundDocumentList',
    'FundUpdateList',
    'CollectionBankAccounts',
  ] as const;

  const [selectedFileType, setSelectedFileType] = React.useState<string | undefined>(undefined);
  const [isResetDialogOpen, setIsResetDialogOpen] = React.useState(false);

  const handleResetFileType = async (fileType: string) => {
    const { data } = await customMutation.mutateAsync({
      method: 'post',
      url: '',
      values: {},
      metaData: {
        operation: 'tomswinSyncResetFileType',
        variables: {
          fileType: {
            type: 'String!',
            value: fileType,
          },
        },
      },
    });

    if (data) {
      invalidate({
        resource: resourceNames.tomswinSyncBatch,
        invalidates: ['list'],
      });
    }
  };

  const handleClearIndex = async (batchId: string) => {
    const { data } = await customMutation.mutateAsync({
      method: 'post',
      url: '',
      values: {},
      metaData: {
        operation: 'tomswinSyncRemoveIndex',
        variables: {
          batchId: {
            type: 'String!',
            value: batchId,
          },
        },
      },
    });

    if (data) {
      // Invalidate the query
      invalidate({
        resource: resourceNames.tomswinSyncBatch,
        invalidates: ['list'],
      });
    }
  };

  const getBatchStatusLabel = (status: string) => {
    if (status === 'Identified') {
      return (
        <span className="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-blue-900 dark:text-blue-300">
          Identified
        </span>
      );
    }
    if (status === 'Indexed') {
      return (
        <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-green-900 dark:text-green-300">
          Indexed
        </span>
      );
    }
    if (status === 'Ignored') {
      return (
        <span className="bg-gray-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-gray-300">
          Ignored
        </span>
      );
    }
    if (status === 'Cleared') {
      return (
        <span className="bg-gray-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-gray-300">
          Cleared
        </span>
      );
    }
    return <span className="text-gray-500">{status}</span>;
  };

  const TomswinSyncBatchesTableComponent = createTableComponent<TomswinSyncBatch>({
    tableVariant: 'simple',
    resource: resourceNames.tomswinSyncBatch,
    fields: [
      'fileLastModifiedAt',
      'batchId',
      'fileType',
      'fileName',
      'indexedLineNo',
      'errorNo',
      'skippedNo',
      'changedNo',
      'status',
      'totalLineNo',
      {
        tomswinSyncCurrent: [
          'lastUpdatedAt',
          'count',
          'pendingCount',
          'failedCount',
          'completedCount',
        ],
      },
    ],
    filterControls: {
      updatedAt: {
        type: 'daterange',
        config: {
          label: 'Batch Date Range',
          placeholder: 'Select date range',
        },
      },
    },
    filterConfig: {
      initial: [{
        field: 'updatedAt',
        operator: 'gte',
        value: new Date().toISOString(),
      }, {
        field: 'updatedAt',
        operator: 'lte',
        value: new Date().toISOString(),
      }]
    },
    columns: () => [
        {
          id: 'fileLastModifiedAt',
          header: 'File date',
          accessorKey: 'fileLastModifiedAt',
          cell: (data) => {
            return dayjs(data.cell.getValue<string>()).format('YYYY-MM-DD HH:mm:ss');
          },
        },
        {
          id: 'batchId',
          header: 'Batch ID',
          accessorKey: 'batchId',
          cell: (data) => {
            return (
              <Link
                target="_blank"
                to={
                  listUrl(resourceNames.tomswinSync) +
                  `?filters[0][field]=batchId&filters[0][operator]=eq&filters[0][value]=${data.cell.getValue<string>()}`
                }
              >
                {data.cell.getValue<string>()}
              </Link>
            );
          },
        },
        {
          id: 'fileName',
          header: 'File Name/Type',
          accessorKey: 'fileName',
          cell: (data) => {
            const fileName = data.cell.getValue<string>();
            const fileType = data.row.original.fileType;
            return `${fileName} ${fileType ? `(${fileType})` : ''}`;
          },
        },
        {
          id: 'status',
          header: 'Status',
          accessorKey: 'status',
          cell: (data) => {
            return getBatchStatusLabel(data.cell.getValue<string>());
          },
        },
        {
          id: 'totalLineNo',
          header: 'Total Line',
          accessorKey: 'totalLineNo',
          cell: (data) => {
            return data.cell.getValue<number>();
          },
        },
        {
          id: 'indexedLineNo',
          header: 'Indexed Line',
          accessorKey: 'indexedLineNo',
          cell: (data) => {
            return data.cell.getValue<number>();
          },
        },
        {
          id: 'errorNo',
          header: 'Error',
          accessorKey: 'errorNo',
          cell: (data) => {
            return data.cell.getValue<number>();
          },
        },
        {
          id: 'skippedNo',
          header: 'Skipped',
          accessorKey: 'skippedNo',
          cell: (data) => {
            return data.cell.getValue<number>();
          },
        },
        {
          id: 'changedNo',
          header: 'Changed',
          accessorKey: 'changedNo',
          cell: (data) => {
            return data.cell.getValue<number>();
          },
        },
        {
          id: 'pendingCount',
          header: 'Pending',
          accessorKey: 'pendingCount',
          cell: (data) => {
            const tomswinSyncCurrent = data.row.original.tomswinSyncCurrent;
            const pendingCount = tomswinSyncCurrent?.pendingCount ?? '-';

            return (
              <span className="text-gray-600">
                {pendingCount}
              </span>
            );
          },
        },
        {
          id: 'failedCount',
          header: 'Failed',
          accessorKey: 'failedCount',
          cell: (data) => {
            const tomswinSyncCurrent = data.row.original.tomswinSyncCurrent;
            const failedCount = tomswinSyncCurrent?.failedCount ?? '-';

            return (
              <span className="text-red-600">
                {failedCount}
              </span>
            );
          },
        },
        {
          id: 'completedCount',
          header: 'Completed',
          accessorKey: 'completedCount',
          cell: (data) => {
            const tomswinSyncCurrent = data.row.original.tomswinSyncCurrent;
            const completedCount = tomswinSyncCurrent?.completedCount ?? '-';

            return (
              <span className="text-green-600">
                {completedCount}
              </span>
            );
          },
        },
        {
          id: 'lastUpdatedAt',
          header: 'Last Updated At',
          accessorKey: 'lastUpdatedAt',
          cell: (data) => {
            const tomswinSyncCurrent = data.row.original.tomswinSyncCurrent;
            const lastUpdatedAt = tomswinSyncCurrent?.lastUpdatedAt;

            if (!lastUpdatedAt || lastUpdatedAt === '') {
              return '-';
            }

            return dayjs(lastUpdatedAt).format('YYYY-MM-DD HH:mm:ss');
          },
        },
        {
          id: 'action',
          header: 'Action',
          accessorKey: 'batchId',
          cell: (data) => {
            if (data.row.original.status !== TomswinSyncBatchStatus.Indexed) {
              return <></>;
            }

            return (
              <AlertDialog.Root>
                <AlertDialog.Trigger asChild>
                  <Button variant="danger">Clear index</Button>
                </AlertDialog.Trigger>
                <AlertDialog.Portal>
                  <AlertDialog.Overlay />
                  <AlertDialog.Content>
                    <AlertDialog.Title>Clear index</AlertDialog.Title>
                    <AlertDialog.Description>
                      This action is undoable. Are you sure you want to proceed?
                    </AlertDialog.Description>
                    <AlertDialog.Footer className="justify-end">
                      <AlertDialog.Cancel>
                        <Button>Cancel</Button>
                      </AlertDialog.Cancel>
                      <AlertDialog.Action>
                        <Button
                          variant="danger"
                          onClick={() => handleClearIndex(data.cell.getValue<string>())}
                        >
                          Clear
                        </Button>
                      </AlertDialog.Action>
                    </AlertDialog.Footer>
                  </AlertDialog.Content>
                </AlertDialog.Portal>
              </AlertDialog.Root>
            );
          },
      },
    ],
  });

  return (
    <div className="w-full">
      <Content title="Developer Tools">
        <div className="w-full grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 xl:grid-cols-1 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle>Tomswin Sync Batches</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-col gap-2">
                <div className="text-sm font-medium text-gray-700">Reset file type</div>
                <div className="text-xs text-gray-500">This will reset indexed state for the selected file type, allowing you to re-sync the full file.</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="min-w-[200px]">
                    <SearchableSelect
                      options={allowedFileTypes.map((ft) => ({ label: ft, value: ft }))}
                      placeholder="Select a file type to reset"
                      onChange={(value) => { value === selectedFileType ? setSelectedFileType(undefined) : setSelectedFileType(value)}}
                      size="sm"
                    />
                  </div>
                  <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={!selectedFileType}
                        onClick={() => setIsResetDialogOpen(true)}
                      >
                        Reset
                      </Button>
                    </DialogTrigger>
                    <DialogPortal>
                      <DialogOverlay />
                      <DialogContent>
                        <DialogTitle>Reset file type</DialogTitle>
                        <DialogDescription>
                          {selectedFileType
                            ? `This will reset indexed state for ${selectedFileType}. Are you sure you want to proceed?`
                            : 'Please select a file type to reset.'}
                        </DialogDescription>
                        <DialogFooter className="justify-end">
                          <Button onClick={() => setIsResetDialogOpen(false)}>Cancel</Button>
                          <Button
                            variant="danger"
                            disabled={!selectedFileType}
                            onClick={() => {
                              if (selectedFileType) {
                                handleResetFileType(selectedFileType);
                                setSelectedFileType(undefined);
                              }
                              setIsResetDialogOpen(false);
                              setSelectedFileType(undefined);
                            }}
                          >
                            Reset
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </DialogPortal>
                  </Dialog>
                </div>
              </div>
              <TomswinSyncBatchesTableComponent />
            </CardContent>
          </Card>
        </div>
      </Content>
    </div>
  );
}
